/** Document edits used by the UI. Every one goes through `edit`, so it can be undone. */
import { newId } from './db';
import { useStore, type Selection } from './store';
import type { Area, Equipment, ProjectDoc, ReferenceValue, ReleaseInput, SceneObject, Source, Substance, Underlay, Vec3 } from '../model/types';

const s = () => useStore.getState();
const BAR = 1e5;

/** Next tag in a series: SR-104 after SR-103. */
export function nextTag(existing: string[], prefix: string): string {
  const nums = existing.map((t) => t.match(new RegExp(`^${prefix}-(\\d+)$`))?.[1]).filter(Boolean).map(Number);
  const n = nums.length ? Math.max(...nums) + 1 : 1;
  return `${prefix}-${String(n).padStart(prefix === 'SR' ? 3 : 2, '0')}`;
}

function areaCentre(doc: ProjectDoc, areaId: string | undefined): Vec3 {
  const a = doc.areas.find((x) => x.id === areaId);
  if (a?.box) return [a.box.center[0], a.box.center[1], 1];
  return [0, 0, 1];
}

function contextArea(doc: ProjectDoc): string | undefined {
  const sel = s().selection;
  if (sel?.kind === 'area') return sel.id;
  if (sel?.kind === 'source') return doc.sources.find((x) => x.id === sel.id)?.areaId;
  if (sel?.kind === 'equipment') return doc.equipment.find((x) => x.id === sel.id)?.areaId;
  return doc.areas[0]?.id;
}

export function defaultRelease(sub: Substance | undefined): ReleaseInput {
  if (sub?.state === 'gas') return { model: 'gas', pressurePaAbs: 5 * BAR + 101_325, temperatureK: 293.15, holeAreaM2: 1e-6, cd: 0.75, z: 1 };
  return { model: 'liquid', pressureDiffPa: 3 * BAR, holeAreaM2: 1e-6, cd: 0.75, vapourFraction: 0.02 };
}

export function addSource(): void {
  const doc = s().doc;
  if (!doc) return;
  const areaId = contextArea(doc);
  if (!areaId) return s().notify('Add an area first: a source of release belongs to an area.');
  const sub = doc.substances[0];
  if (!sub) return s().notify('Add a substance first.');
  const id = newId();
  const src: Source = {
    id, tag: nextTag(doc.sources.map((x) => x.tag), 'SR'), description: 'New source of release', areaId, substanceId: sub.id,
    position: areaCentre(doc, areaId), grade: 'secondary', releaseForm: (sub.relativeVapourDensity ?? 1) > 1 ? 'heavy' : 'diffusive',
    release: defaultRelease(sub),
  };
  s().edit(`Add ${src.tag}`, (d) => { d.sources.push(src); });
  s().select({ kind: 'source', id });
}

export function addEquipment(): void {
  const doc = s().doc;
  if (!doc) return;
  const areaId = contextArea(doc);
  const id = newId();
  const [x, y] = areaCentre(doc, areaId);
  const eq: Equipment = {
    id, tag: nextTag(doc.equipment.map((e) => e.tag), 'EQ'), description: 'New equipment', ...(areaId ? { areaId } : {}),
    box: { center: [x + 1, y, 0.5], size: [0.4, 0.4, 0.4], yawRad: 0 }, ex: { kind: 'non-ex' },
  };
  s().edit(`Add ${eq.tag}`, (d) => { d.equipment.push(eq); });
  s().select({ kind: 'equipment', id });
}

export function addArea(kind: Area['kind']): void {
  const doc = s().doc;
  if (!doc) return;
  const id = newId();
  const maxX = Math.max(0, ...doc.areas.map((a) => (a.box ? a.box.center[0] + a.box.size[0] / 2 : 0)));
  const area: Area = kind === 'indoor'
    ? {
        id, name: `Room ${doc.areas.filter((a) => a.kind === 'indoor').length + 1}`, kind,
        box: { center: [maxX + 6, 0, 1.5], size: [6, 5, 3], yawRad: 0 },
        ventilation: { kind: 'indoor', type: 'artificial-general', airFlowM3PerS: 0.5, flowDirection: [1, 0, 0], velocityReductionFactor: 1, inefficiencyF: 2 },
        availability: { level: 'fair' }, summation: { maxSimultaneousPrimary: 'all' },
      }
    : {
        id, name: `Outdoor area ${doc.areas.filter((a) => a.kind === 'outdoor').length + 1}`, kind,
        box: { center: [maxX + 8, 0, 0.5], size: [10, 10, 1], yawRad: 0 },
        ventilation: { kind: 'outdoor', velocityMPerS: 0.5, obstructed: false },
        availability: { level: 'good' },
      };
  s().edit(`Add ${area.name}`, (d) => { d.areas.push(area); });
  s().select({ kind: 'area', id });
}

export function addObject(kind: SceneObject['kind']): void {
  const doc = s().doc;
  if (!doc) return;
  const id = newId();
  const [x, y] = areaCentre(doc, contextArea(doc));
  const n = doc.objects.filter((o) => o.kind === kind).length + 1;
  const obj: SceneObject = kind === 'box'
    ? { id, name: `Box ${n}`, kind, box: { center: [x, y + 2, 0.5], size: [2, 1, 1], yawRad: 0 } }
    : { id, name: `Vessel ${n}`, kind, base: [x, y + 2, 0], radiusM: 1, heightM: 3 };
  s().edit(`Add ${obj.name}`, (d) => { d.objects.push(obj); });
  s().select({ kind: 'object', id });
}

/** Reads an image file, downscales it to at most 3000 px, and adds it as a site plan underlay. */
export async function addUnderlay(file: File): Promise<void> {
  const doc = s().doc;
  if (!doc) return;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('unreadable'));
      i.src = url;
    });
    const scale = Math.min(1, 3000 / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const image = file.type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.88);
    const id = newId();
    const underlay: Underlay = { id, name: file.name.replace(/\.[^.]+$/, ''), image, pixelWidth: w, pixelHeight: h, center: [0, 0], widthM: 50, rotationRad: 0, opacity: 0.8, visible: true };
    s().edit('Add site plan', (d) => { d.underlays = [...(d.underlays ?? []), underlay]; });
    s().select({ kind: 'underlay', id });
    s().notify('Plan added at 50 m wide. Enter its real width in the inspector to set the scale.');
  } catch {
    s().notify(`${file.name} could not be read as an image. Use PNG or JPEG; export PDF drawings to PNG first.`);
  } finally {
    URL.revokeObjectURL(url);
  }
}

const LIBRARY_KEY = 'extent:reference-values';

/** The engineer's saved values, kept in this browser so new projects start with them. */
export function savedLibrary(): ReferenceValue[] {
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is ReferenceValue => typeof v === 'object' && v !== null && typeof (v as ReferenceValue).label === 'string' && Number.isFinite((v as ReferenceValue).valueSi));
  } catch {
    return [];
  }
}

function rememberLibrary(values: ReferenceValue[]): void {
  try {
    localStorage.setItem(LIBRARY_KEY, JSON.stringify(values.slice(0, 200)));
  } catch {
    /* storage blocked; the project still holds them */
  }
}

export function addReferenceValue(kind: ReferenceValue['kind'], label: string, valueSi: number): void {
  const doc = s().doc;
  if (!doc) return;
  const name = label.trim();
  if (!name) return;
  const existing = (doc.settings.referenceValues ?? []).find((v) => v.kind === kind && v.label.toLowerCase() === name.toLowerCase());
  const value: ReferenceValue = { id: existing?.id ?? newId(), kind, label: name, valueSi };
  s().edit(existing ? `Update ${name}` : `Save ${name}`, (d) => {
    const list = d.settings.referenceValues ?? (d.settings.referenceValues = []);
    const i = list.findIndex((v) => v.id === value.id);
    if (i >= 0) list[i] = value; else list.push(value);
  });
  rememberLibrary([...(useStore.getState().doc?.settings.referenceValues ?? [])]);
}

export function removeReferenceValue(id: string): void {
  const doc = s().doc;
  if (!doc) return;
  const gone = doc.settings.referenceValues?.find((v) => v.id === id);
  s().edit(`Remove ${gone?.label ?? 'value'}`, (d) => {
    d.settings.referenceValues = (d.settings.referenceValues ?? []).filter((v) => v.id !== id);
  });
  rememberLibrary([...(useStore.getState().doc?.settings.referenceValues ?? [])]);
}

export function addSubstance(): void {
  const id = newId();
  const sub: Substance = { id, name: 'New substance', state: 'liquid', molarMassKgPerKmol: 100, lflVolFrac: 0.01, dataSources: {} };
  s().edit('Add substance', (d) => { d.substances.push(sub); });
  s().select({ kind: 'substance', id });
  s().setView({ dockTab: 'substances', dockOpen: true });
}

const label = (doc: ProjectDoc, sel: NonNullable<Selection>): string => {
  switch (sel.kind) {
    case 'source': return doc.sources.find((x) => x.id === sel.id)?.tag ?? 'source';
    case 'equipment': return doc.equipment.find((x) => x.id === sel.id)?.tag ?? 'equipment';
    case 'area': return doc.areas.find((x) => x.id === sel.id)?.name ?? 'area';
    case 'substance': return doc.substances.find((x) => x.id === sel.id)?.name ?? 'substance';
    case 'object': return doc.objects.find((x) => x.id === sel.id)?.name ?? 'object';
    case 'underlay': return doc.underlays?.find((x) => x.id === sel.id)?.name ?? 'site plan';
  }
};

export function deleteSelection(): void {
  const { doc, selection } = s();
  if (!doc || !selection) return;
  const name = label(doc, selection);
  if (selection.kind === 'area' && doc.sources.some((x) => x.areaId === selection.id)) {
    return s().notify(`${name} still has sources of release. Move or delete them first.`);
  }
  if (selection.kind === 'substance' && doc.sources.some((x) => x.substanceId === selection.id)) {
    return s().notify(`${name} is used by sources of release. Change them first.`);
  }
  s().edit(`Delete ${name}`, (d) => {
    const drop = <T extends { id: string }>(arr: T[]) => arr.filter((x) => x.id !== selection.id);
    if (selection.kind === 'source') d.sources = drop(d.sources);
    if (selection.kind === 'equipment') d.equipment = drop(d.equipment);
    if (selection.kind === 'area') {
      d.areas = drop(d.areas);
      for (const e of d.equipment) if (e.areaId === selection.id) delete e.areaId;
    }
    if (selection.kind === 'substance') d.substances = drop(d.substances);
    if (selection.kind === 'object') d.objects = drop(d.objects);
    if (selection.kind === 'underlay') d.underlays = drop(d.underlays ?? []);
  });
  s().select(null);
  s().notify(`Deleted ${name}. Ctrl+Z to undo.`);
}

export function duplicateSelection(): void {
  const { doc, selection } = s();
  if (!doc || !selection) return;
  const id = newId();
  if (selection.kind === 'source') {
    const src = doc.sources.find((x) => x.id === selection.id);
    if (!src) return;
    const copy: Source = { ...structuredClone(src), id, tag: nextTag(doc.sources.map((x) => x.tag), 'SR'), position: [src.position[0] + 1, src.position[1], src.position[2]] };
    s().edit(`Duplicate ${src.tag}`, (d) => { d.sources.push(copy); });
    s().select({ kind: 'source', id });
  } else if (selection.kind === 'equipment') {
    const eq = doc.equipment.find((x) => x.id === selection.id);
    if (!eq) return;
    const copy: Equipment = { ...structuredClone(eq), id, tag: nextTag(doc.equipment.map((x) => x.tag), eq.tag.split('-')[0] || 'EQ'), box: { ...eq.box, center: [eq.box.center[0] + 1, eq.box.center[1], eq.box.center[2]] } };
    s().edit(`Duplicate ${eq.tag}`, (d) => { d.equipment.push(copy); });
    s().select({ kind: 'equipment', id });
  }
}
